import { quantizeLayoutAspect } from './constants';

/** Cross-island handoffs. Symmetric: `links[a][b]` = wires between those nebulas. */
export function buildIslandLinks(clusterCount, clusterIndex, depEdges) {
  const links = Array.from({ length: clusterCount }, () => new Float64Array(clusterCount));
  if (!clusterCount || !depEdges?.length) return links;
  depEdges.forEach(([a, b]) => {
    const ca = clusterIndex[a];
    const cb = clusterIndex[b];
    if (ca === cb || ca < 0 || cb < 0) return;
    links[ca][cb] += 1;
    links[cb][ca] += 1;
  });
  return links;
}

/**
 * Pack islands from the centre out. `aspect` > 1 prefers a wide ellipse so
 * the constellation matches a landscape window; < 1 prefers a tall one.
 *
 * Seats stay on that ellipse so the graph still fills the window. Handoffs
 * are scored in the same ellipse space — neighbouring angles, not the
 * short axis — so links cannot squash the stretch. A 2-opt swap then
 * unkinks leftover crossings. Tiny leftovers still walk inward so a lone
 * untagged island does not ride the outer ring.
 */
export function packCircles(radii, rnd, aspect = 1, links = null) {
  const n = radii.length;
  const cx = new Float64Array(n);
  const cy = new Float64Array(n);
  if (n <= 1) return { cx, cy };
  const ax = quantizeLayoutAspect(aspect);
  const gap = 0.95;
  const sep = (dx, dy) => Math.hypot(dx / ax, dy);
  const order = radii.map((r, i) => i).sort((a, b) => radii[b] - radii[a] || a - b);
  cx[order[0]] = 0;
  cy[order[0]] = 0;
  for (let k = 1; k < n; k += 1) {
    const i = order[k];
    let blobR = 0;
    for (let t = 0; t < k; t += 1) {
      const j = order[t];
      blobR = Math.max(blobR, sep(cx[j], cy[j]) + radii[j]);
    }
    const tryR = blobR + radii[i] * 0.22 + gap;
    let bestX = 0; let bestY = 0; let bestScore = Infinity;
    const spin = rnd() * Math.PI * 2;
    for (let s = 0; s < 80; s += 1) {
      const ang = spin + (s / 80) * Math.PI * 2;
      const tx = Math.cos(ang) * tryR * ax;
      const ty = Math.sin(ang) * tryR;
      let hit = false;
      let slack = Infinity;
      for (let t = 0; t < k; t += 1) {
        const j = order[t];
        const d = Math.hypot(tx - cx[j], ty - cy[j]);
        const need = radii[i] + radii[j] + gap;
        if (d < need) { hit = true; break; }
        slack = Math.min(slack, d - need);
      }
      if (hit) continue;
      const score = sep(tx, ty) - slack * 0.25 + attractAt(i, tx, ty, links, cx, cy, sep, order, k) * 0.45;
      if (score < bestScore) { bestScore = score; bestX = tx; bestY = ty; }
    }
    if (bestScore === Infinity) {
      const ang = spin + k;
      cx[i] = Math.cos(ang) * (tryR + radii[i]) * ax;
      cy[i] = Math.sin(ang) * (tryR + radii[i]);
    } else {
      cx[i] = bestX;
      cy[i] = bestY;
    }
  }
  // Close huge leftover holes, then only push — never pull so tight they kiss.
  for (let pass = 0; pass < 28; pass += 1) {
    for (let i = 0; i < n; i += 1) { cx[i] *= 0.994; cy[i] *= 0.994; }
    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        let dx = cx[i] - cx[j]; let dy = cy[i] - cy[j];
        let dist = Math.hypot(dx, dy);
        const need = radii[i] + radii[j] + gap;
        if (dist < 1e-4) { dx = 1; dy = 0; dist = 1; }
        const ux = dx / dist; const uy = dy / dist;
        if (dist < need) {
          const push = (need - dist) * 0.5;
          cx[i] += ux * push; cy[i] += uy * push;
          cx[j] -= ux * push; cy[j] -= uy * push;
        } else if (dist > need + 2.4) {
          const pull = Math.min((dist - need - 2.4) * 0.12, 0.22);
          cx[i] -= ux * pull; cy[i] -= uy * pull;
          cx[j] += ux * pull; cy[j] += uy * pull;
        }
      }
    }
  }
  resolveOverlaps(radii, cx, cy, gap);
  uncrossLinked(radii, cx, cy, links, gap, sep);
  tuckSmallIslands(radii, cx, cy, ax, gap, links, sep);
  resolveOverlaps(radii, cx, cy, gap);
  return { cx, cy };
}

function attractAt(i, tx, ty, links, cx, cy, sep, order, placed) {
  if (!links) return 0;
  let s = 0;
  if (order && placed != null) {
    for (let t = 0; t < placed; t += 1) {
      const j = order[t];
      const w = links[i][j];
      if (w) s += w * sep(tx - cx[j], ty - cy[j]);
    }
    return s;
  }
  const n = cx.length;
  for (let j = 0; j < n; j += 1) {
    if (j === i) continue;
    const w = links[i][j];
    if (w) s += w * sep(tx - cx[j], ty - cy[j]);
  }
  return s;
}

function linkCost(cx, cy, links, sep) {
  let len = 0;
  const segs = [];
  const n = cx.length;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const w = links[i][j];
      if (!w) continue;
      len += w * sep(cx[i] - cx[j], cy[i] - cy[j]);
      segs.push([i, j]);
    }
  }
  let crosses = 0;
  for (let a = 0; a < segs.length; a += 1) {
    for (let b = a + 1; b < segs.length; b += 1) {
      const [i, j] = segs[a];
      const [k, l] = segs[b];
      if (i === k || i === l || j === k || j === l) continue;
      if (segmentsCross(cx[i], cy[i], cx[j], cy[j], cx[k], cy[k], cx[l], cy[l])) crosses += 1;
    }
  }
  return len + crosses * 8;
}

function segmentsCross(ax, ay, bx, by, cx, cy, dx, dy) {
  const side = (px, py, qx, qy, rx, ry) => (qx - px) * (ry - py) - (qy - py) * (rx - px);
  const d1 = side(ax, ay, bx, by, cx, cy);
  const d2 = side(ax, ay, bx, by, dx, dy);
  const d3 = side(cx, cy, dx, dy, ax, ay);
  const d4 = side(cx, cy, dx, dy, bx, by);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

function hasOverlap(radii, cx, cy, gap) {
  const n = radii.length;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      if (Math.hypot(cx[i] - cx[j], cy[i] - cy[j]) < radii[i] + radii[j] + gap - 1e-6) return true;
    }
  }
  return false;
}

/** Swap similar-sized seats when that shortens handoffs or unkinks a crossing. */
function uncrossLinked(radii, cx, cy, links, gap, sep) {
  if (!links) return;
  const n = radii.length;
  let best = linkCost(cx, cy, links, sep);
  let improved = true;
  for (let guard = 0; improved && guard < 10; guard += 1) {
    improved = false;
    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        const big = Math.max(radii[i], radii[j], 1);
        if (Math.abs(radii[i] - radii[j]) > 0.28 * big) continue;
        const x0 = cx[i]; const y0 = cy[i];
        cx[i] = cx[j]; cy[i] = cy[j];
        cx[j] = x0; cy[j] = y0;
        if (hasOverlap(radii, cx, cy, gap)) {
          cx[j] = cx[i]; cy[j] = cy[i];
          cx[i] = x0; cy[i] = y0;
          continue;
        }
        const next = linkCost(cx, cy, links, sep);
        if (next + 1e-6 < best) {
          best = next;
          improved = true;
        } else {
          cx[j] = cx[i]; cy[j] = cy[i];
          cx[i] = x0; cy[i] = y0;
        }
      }
    }
  }
}

function resolveOverlaps(radii, cx, cy, gap) {
  const n = radii.length;
  for (let pass = 0; pass < 20; pass += 1) {
    let moved = false;
    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        let dx = cx[i] - cx[j]; let dy = cy[i] - cy[j];
        let dist = Math.hypot(dx, dy);
        const need = radii[i] + radii[j] + gap;
        if (dist < 1e-4) { dx = 1; dy = 0; dist = 1; }
        if (dist >= need) continue;
        moved = true;
        const push = (need - dist) * 0.5;
        const ux = dx / dist; const uy = dy / dist;
        cx[i] += ux * push; cy[i] += uy * push;
        cx[j] -= ux * push; cy[j] -= uy * push;
      }
    }
    if (!moved) break;
  }
}

/** Small leftovers walk inward in ellipse-space. Large blobs keep the window stretch. */
function tuckSmallIslands(radii, cx, cy, ax, gap, links, sep) {
  const n = radii.length;
  if (n < 3) return;
  const maxR = Math.max(...radii);
  const cut = maxR * 0.45;
  for (let i = 0; i < n; i += 1) {
    if (radii[i] > cut) continue;
    const cur = Math.hypot(cx[i] / ax, cy[i]);
    if (cur < 1e-4) continue;
    const linked = !!links && attractAt(i, cx[i], cy[i], links, cx, cy, sep) > 0;
    const scoreOf = (tx, ty) => {
      const base = Math.hypot(tx / ax, ty);
      if (!linked) return base;
      return attractAt(i, tx, ty, links, cx, cy, sep) + base * 0.15;
    };
    let bestX = cx[i];
    let bestY = cy[i];
    let best = scoreOf(cx[i], cy[i]);
    for (let ring = 0; ring < 12; ring += 1) {
      const tryR = (ring / 12) * cur;
      for (let s = 0; s < 48; s += 1) {
        const ang = (s / 48) * Math.PI * 2;
        const tx = Math.cos(ang) * tryR * ax;
        const ty = Math.sin(ang) * tryR;
        let hit = false;
        for (let j = 0; j < n; j += 1) {
          if (j === i) continue;
          if (Math.hypot(tx - cx[j], ty - cy[j]) < radii[i] + radii[j] + gap) {
            hit = true;
            break;
          }
        }
        if (hit) continue;
        const score = scoreOf(tx, ty);
        if (score < best) {
          best = score;
          bestX = tx;
          bestY = ty;
        }
      }
    }
    cx[i] = bestX;
    cy[i] = bestY;
  }
}
